/**
 * 附件直传 OSS 业务层 — GET /attachment/getToken + PUT 预签名 URL
 */

import { attachmentService } from '@/services/artApi';
import { unwrapApiResult } from '@/utils/artMapper';

/** 当前 art 服务可用的业务类型（文档：1-订单编号） */
export const ATTACHMENT_BUSINESS_TYPE = {
  ORDER: 1,
};

export const ATTACHMENT_FOLDER = {
  MATERIAL: 'material',
  FONT: 'font',
  TEMPLATE: 'template',
};

function sanitizeFilename(name = 'file') {
  const base = String(name).split(/[/\\]/).pop() || 'file';
  return base.replace(/[^\w.\u4e00-\u9fff-]+/g, '_').replace(/^\.+/, '') || 'file';
}

function uniqueFilename(name) {
  const safe = sanitizeFilename(name);
  const dot = safe.lastIndexOf('.');
  const stamp = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  if (dot <= 0) return `${stamp}_${safe}`;
  return `${safe.slice(0, dot)}_${stamp}${safe.slice(dot)}`;
}

function toPublicUrl(signUrl) {
  const raw = String(signUrl || '');
  const q = raw.indexOf('?');
  return q >= 0 ? raw.slice(0, q) : raw;
}

/**
 * 申请 OSS 预签名上传凭证
 * @param {{ filename: string, businessId: string|number, businessType?: number, folder?: string }} params
 */
export async function fetchOssUploadToken(params = {}) {
  if (!params.filename) throw new Error('缺少上传文件名');
  if (params.businessId == null || params.businessId === '') {
    throw new Error('缺少 businessId');
  }
  const payload = await attachmentService.getToken({
    filename: params.filename,
    businessId: String(params.businessId),
    businessType: params.businessType ?? ATTACHMENT_BUSINESS_TYPE.ORDER,
    folder: params.folder || undefined,
  });
  const result = unwrapApiResult(payload) || {};
  const sign = result.sign || result.url || result.uploadUrl || result.presignedUrl;
  if (!sign) throw new Error('未返回 OSS 预签名地址');
  return {
    sign,
    uploadMethod: String(result.uploadMethod || 'PUT').toUpperCase(),
    headers: result.headers && typeof result.headers === 'object' ? { ...result.headers } : {},
    expiresAt: result.expiresAt,
    raw: result,
  };
}

/**
 * 浏览器高性能直传文件到 OSS，支持实时进度监听、传输测速与网络重试
 * @param {File|Blob} file
 * @param {Object} options
 * @param {string} [options.filename]
 * @param {string|number} [options.businessId]
 * @param {number} [options.businessType]
 * @param {string} [options.folder]
 * @param {AbortSignal} [options.signal]
 * @param {Function} [options.onProgress] ({ percent, speed, loaded, total, formattedSpeed }) => void
 * @param {number} [options.retries=1]
 */
export async function uploadFileToOss(file, options = {}) {
  if (!file) throw new Error('缺少上传文件');
  const filename = uniqueFilename(options.filename || file.name || 'file');
  const token = await fetchOssUploadToken({
    filename,
    businessId: options.businessId,
    businessType: options.businessType,
    folder: options.folder,
  });

  const headers = { ...token.headers };
  if (!headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = file.type || 'application/octet-stream';
  }

  const maxRetries = options.retries ?? 1;
  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (options.signal?.aborted) {
      throw Object.assign(new Error('上传已取消'), { cancelled: true });
    }

    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const startTime = Date.now();
        let lastLoaded = 0;
        let lastTime = startTime;

        if (options.signal) {
          const onAbort = () => {
            xhr.abort();
            reject(Object.assign(new Error('上传已取消'), { cancelled: true }));
          };
          options.signal.addEventListener('abort', onAbort, { once: true });
          xhr.addEventListener('loadend', () => {
            options.signal?.removeEventListener?.('abort', onAbort);
          }, { once: true });
        }

        if (typeof options.onProgress === 'function') {
          xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;
            const now = Date.now();
            const timeDiff = (now - lastTime) / 1000;
            let currentSpeed = 0;
            if (timeDiff >= 0.2) {
              currentSpeed = (event.loaded - lastLoaded) / timeDiff;
              lastLoaded = event.loaded;
              lastTime = now;
            } else if (event.loaded > 0) {
              const totalElapsed = (now - startTime) / 1000;
              currentSpeed = totalElapsed > 0 ? event.loaded / totalElapsed : 0;
            }
            const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
            const formattedSpeed = currentSpeed > 1048576
              ? `${(currentSpeed / 1048576).toFixed(1)} MB/s`
              : `${Math.round(currentSpeed / 1024)} KB/s`;

            options.onProgress({
              percent,
              loaded: event.loaded,
              total: event.total,
              speed: currentSpeed,
              formattedSpeed,
            });
          };
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(xhr.responseText || `OSS 上传失败: ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('OSS 网络连接失败，请检查网络'));
        xhr.ontimeout = () => reject(new Error('OSS 上传连接超时'));

        xhr.open(token.uploadMethod || 'PUT', token.sign, true);
        Object.entries(headers).forEach(([key, val]) => {
          if (val != null && val !== '') xhr.setRequestHeader(key, String(val));
        });
        xhr.send(file);
      });

      return {
        url: toPublicUrl(token.sign),
        filename,
        fileSize: file.size,
        contentType: headers['Content-Type'] || file.type || '',
        expiresAt: token.expiresAt,
      };
    } catch (err) {
      if (err?.cancelled || options.signal?.aborted) throw err;
      lastError = err;
      if (attempt < maxRetries) {
        await new Promise(r => setTimeout(r, 400 * (attempt + 1)));
      }
    }
  }

  throw lastError || new Error('OSS 上传重试失败');
}

export async function uploadMaterialFileToOss(file, businessId, extraOptions = {}) {
  return uploadFileToOss(file, {
    businessId,
    folder: ATTACHMENT_FOLDER.MATERIAL,
    businessType: ATTACHMENT_BUSINESS_TYPE.ORDER,
    ...extraOptions,
  });
}

export async function uploadFontFileToOss(file, businessId = 0, extraOptions = {}) {
  return uploadFileToOss(file, {
    businessId,
    folder: ATTACHMENT_FOLDER.FONT,
    businessType: ATTACHMENT_BUSINESS_TYPE.ORDER,
    ...extraOptions,
  });
}

export async function uploadTemplateFileToOss(file, businessId, extraOptions = {}) {
  return uploadFileToOss(file, {
    businessId,
    folder: ATTACHMENT_FOLDER.TEMPLATE,
    businessType: ATTACHMENT_BUSINESS_TYPE.ORDER,
    ...extraOptions,
  });
}
